import React, { useEffect } from 'react';
import { Modal, Form, TreeSelect, Button, Tooltip } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import styles from './index.module.scss';
import { getOptionLabel, toastText } from 'utils/utils';
interface BatchEditModalProps {
    visible: boolean;
    onCancel: () => void;
    onUpdate: (values: any) => void;
    accountOptions: any[];
    customerOptions: any[];
    classOptions: any[];
    locationOptions: any[];
    isLoading?: boolean;
}

const BatchEditModal: React.FC<BatchEditModalProps> = ({
    visible,
    onCancel,
    onUpdate,
    accountOptions,
    customerOptions,
    classOptions,
    locationOptions,
    isLoading = false,
}) => {
    const [form] = Form.useForm();

    // Reset form when modal closes
    useEffect(() => {
        if (!visible) {
            form.resetFields();
        }
    }, [visible, form]);

    const handleUpdate = () => {
        form.validateFields().then((values) => {
            // Validate that at least one field is selected
            if (!values.account && !values.customer && !values.class && !values.location) {
                toastText('Please select at least one field to update', 'error');
                return;
            }

            onUpdate(values);
        }).catch((errorInfo) => {
            console.log('Validation failed:', errorInfo);
        });
    };

    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    return (
        <Modal
            title="Batch Edit Transactions"
            open={visible}
            onCancel={handleCancel}
            footer={null}
            className={styles['batch-edit-modal']}
            width={500}
            centered
            maskClosable={false}
        >
            <div className={styles['modal-content']}>
                <p className={styles['modal-description']}>
                    Edit transactions in <b>QuickBooks</b>. Only fields you select will be updated.
                </p>
                <div style={{
                    marginBottom: '24px',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffeeba',
                    borderRadius: '8px',
                    padding: '12px',
                    fontSize: '12px'
                }}>
                    <p style={{
                        margin: 0,
                        color: '#856404',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <WarningOutlined />
                        Please ensure that transactions are synced before performing a batch edit.
                    </p>
                </div>

                <Form form={form} layout="vertical" className={styles['batch-form']}>
                    <Form.Item
                        label="Account"
                        name="account"
                        className={styles['form-item']}
                    >
                        <TreeSelect
                            placeholder="Select account (optional)"
                            allowClear
                            showSearch
                            treeData={accountOptions}
                            className={styles['form-select']}
                            treeLine
                            treeDefaultExpandAll
                            filterTreeNode={(inputValue, treeNode) =>
                                treeNode?.props?.title
                                    ?.toLowerCase()
                                    .includes(inputValue.toLowerCase())
                            }
                            maxTagPlaceholder={() => {
                                const selectedValue = form.getFieldValue('account');
                                if (!selectedValue) return "Select account";
                                const label = getOptionLabel(selectedValue, accountOptions);
                                return (
                                    <Tooltip title={label} placement="topLeft">
                                        <span className={styles['ellipsis-text']}>
                                            {label}
                                        </span>
                                    </Tooltip>
                                );
                            }}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Customer"
                        name="customer"
                        className={styles['form-item']}
                    >
                        <TreeSelect
                            placeholder="Select customer (optional)"
                            allowClear
                            showSearch
                            treeData={customerOptions}
                            className={styles['form-select']}
                            treeLine
                            treeDefaultExpandAll
                            filterTreeNode={(inputValue, treeNode) =>
                                treeNode?.props?.title
                                    ?.toLowerCase()
                                    .includes(inputValue.toLowerCase())
                            }
                            maxTagPlaceholder={() => {
                                const selectedValue = form.getFieldValue('customer');
                                if (!selectedValue) return "Select customer";
                                const label = getOptionLabel(selectedValue, customerOptions);
                                return (
                                    <Tooltip title={label} placement="topLeft">
                                        <span className={styles['ellipsis-text']}>
                                            {label}
                                        </span>
                                    </Tooltip>
                                );
                            }}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Class"
                        name="class"
                        className={styles['form-item']}
                    >
                        <TreeSelect
                            placeholder="Select class (optional)"
                            allowClear
                            showSearch
                            treeData={classOptions}
                            className={styles['form-select']}
                            treeLine
                            treeDefaultExpandAll
                            filterTreeNode={(inputValue, treeNode) =>
                                treeNode?.props?.title
                                    ?.toLowerCase()
                                    .includes(inputValue.toLowerCase())
                            }
                            maxTagPlaceholder={() => {
                                const selectedValue = form.getFieldValue('class');
                                if (!selectedValue) return "Select class";
                                const label = getOptionLabel(selectedValue, classOptions);
                                return (
                                    <Tooltip title={label} placement="topLeft">
                                        <span className={styles['ellipsis-text']}>
                                            {label}
                                        </span>
                                    </Tooltip>
                                );
                            }}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Location"
                        name="location"
                        className={styles['form-item']}
                    >
                        <TreeSelect
                            placeholder="Select location (optional)"
                            allowClear
                            showSearch
                            treeData={locationOptions}
                            className={styles['form-select']}
                            treeLine
                            treeDefaultExpandAll
                            filterTreeNode={(inputValue, treeNode) =>
                                treeNode?.props?.title
                                    ?.toLowerCase()
                                    .includes(inputValue.toLowerCase())
                            }
                            maxTagPlaceholder={() => {
                                const selectedValue = form.getFieldValue('location');
                                if (!selectedValue) return "Select location";
                                const label = getOptionLabel(selectedValue, locationOptions);
                                return (
                                    <Tooltip title={label} placement="topLeft">
                                        <span className={styles['ellipsis-text']}>
                                            {label}
                                        </span>
                                    </Tooltip>
                                );
                            }}
                        />
                    </Form.Item>
                </Form>

                <div className={styles['modal-actions']}>
                    <Button
                        onClick={handleCancel}
                        className={`btn-black ${styles['cancel-button']}`}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleUpdate}
                        loading={isLoading}
                        className={`btn-black ${styles['update-button']}`}
                    >
                        Update Transactions
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default BatchEditModal;
